// How the browser GETS a sketch, and the two lanes it can arrive on.
//
// The SHAPE of a sketch lives in `lib/design-scene.ts` and is re-exported from
// here, so no import path in `focus/` had to move when the chat fold needed to
// parse one too. Read that file's header for what a `DesignScene` is and why it
// carries no layer name.
//
// TWO LANES, ONE SHAPE.
//
//   PERSISTED FIRST. A `figma_scene` event rides the chat's own bus and is
//   replayed in full on every attach, so a restored chat holds the picture that
//   conversation actually had. When a host can hand one in (`persisted`), this
//   hook does not fetch at all: no request, no `unknown_link` after an instance
//   recycle, no `no_raw` after the raw bytes' 7-day horizon, and — the point of
//   the whole change — no newer bytes. It is the chat's own copy.
//
//   THE GET SECOND. `GET …/figma/preview/:previewId` is the FALLBACK, and it is
//   still correct for everything it was correct for before: a chat from before
//   the event existed, a scene the event log's FIFO cap evicted, a
//   `sceneVersion` this bundle refuses, an older client. What it serves is
//   Loom's NEWEST stored copy of the frame, which is not necessarily the one
//   this conversation read — hence the dated caption, which is true on that
//   lane and now literally true on the other.
//
// BOTH LANES TELL THE MODEL. "The user is looking at a drawing" is a fact the
// model has no other way to learn — the picture is not in its context — and
// getting it wrong is a defect that has shipped once already: asked about an
// element Loom itself drew, the model denied it existed. The GET queues that
// notice server-side as a side effect of serving. The persisted lane has no
// GET, so it POSTs `…/:previewId/shown` instead. A signal that rides on a
// request disappears when the request does; this one is said on purpose.
//
// THE TWO MUST SHOW THE SAME SHAPE, and they do by construction: `readAtIso`
// rides INSIDE the scene on both lanes (the event carries the store row's
// `read_at` stamped into the draw list the GET would have served), so the
// caption code never learns which lane served it and cannot phrase the two
// differently. That is the whole reason the stamp is a scene field rather than
// a sibling of it on the event.

import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { HttpError, httpGet, httpPost } from "../../lib/http";
import { designSceneSchema, type DesignScene } from "../../lib/design-scene";

// Re-exported so every existing import of this module keeps working — the
// shape moved, the surface did not.
export {
  asHexColor,
  designSceneSchema,
  SCENE_VERSION,
} from "../../lib/design-scene";
export type {
  DesignScene,
  SceneCounts,
  SceneGradient,
  SceneKind,
  SceneNode,
} from "../../lib/design-scene";

/** How tall the drawing may be inside a FLOW step, where a question is on the
 *  screen with it. Deliberately smaller than the chat budget: the question is
 *  what the user is answering, and the sketch must never push it off screen. */
export const PREVIEW_FLOW_MAX_H = 400;

/** How tall the drawing may be in chat, where nothing competes for the fold. */
export const PREVIEW_CHAT_MAX_H = 520;

/** A frame taller than this many times its width cannot be fitted height-first
 *  without collapsing to an illegible ribbon (375 x 2400 at 400px tall is 63px
 *  wide). Past it the fit switches to width-first and crops. */
export const PREVIEW_TALL_RATIO = 2.2;

/**
 * `empty`  — there is no drawing for this design and there never will be for
 *            this request (the bytes are past their horizon, or the projector
 *            refused).
 * `lost`   — Loom can no longer say which screen this handle was about. See
 *            {@link LOST_HANDLE_REASONS}: a different sentence, because the
 *            other one is false here.
 * `failed` — something went wrong. The words Loom read are still there.
 */
export type PreviewStatus = "loading" | "ready" | "empty" | "lost" | "failed";

export interface DesignSceneState {
  readonly status: PreviewStatus;
  readonly scene: DesignScene | null;
}

/**
 * Where a sketch comes from. ONE path segment, and the client never builds it:
 * `previewId` is an opaque token a host was HANDED — the ask's own
 * `subject.previewId`, or the chip's `linkId` — which is the same discipline
 * `figma_allow_file` follows (the client names a token, never a file key).
 */
export const designScenePath = (
  sessionId: string,
  previewId: string,
): string =>
  `/api/sessions/${encodeURIComponent(sessionId)}/figma/preview/${encodeURIComponent(previewId)}`;

/**
 * Where "the user is looking at this drawing" is said OUT LOUD.
 *
 * The model cannot see the picture, and told nothing it denies that an element
 * Loom itself drew is in the design. The server's answer to that is
 * `buildPreviewShownNotice` — and the only thing that had ever queued it was
 * the GET above, as a side effect of serving the bytes. A chat that carries its
 * own scene never issues that GET, so the notice rode away with the request:
 * the model stopped being told, for every chat with a stored picture, which is
 * every new one.
 *
 * So the stored lane says it itself. The POST carries NO BODY and nothing about
 * the picture: it names the handle this host was handed, and the server reads
 * the counts off its OWN copy of the `figma_scene` event — the same event the
 * browser drew. Nothing the client believes about the drawing reaches the
 * prompt.
 */
export const designSceneShownPath = (
  sessionId: string,
  previewId: string,
): string => `${designScenePath(sessionId, previewId)}/shown`;

/** The signal route's answer. Parsed rather than ignored so a proxy that
 *  swallows the route and returns an HTML page is a caught failure, not a
 *  silent success. */
const shownAckSchema = z.object({ noted: z.boolean() });

/**
 * Fire-and-forget: tell the server the sketch is on screen.
 *
 * NEVER throws and never resolves to anything a caller acts on. A drawing the
 * model was not told about is a worse conversation; a drawing that fails to
 * render because a beacon 404'd is a broken screen, and the second is the one
 * this must not be able to cause.
 */
const notePreviewShown = async (
  sessionId: string,
  previewId: string,
): Promise<void> => {
  try {
    await httpPost(
      designSceneShownPath(sessionId, previewId),
      {},
      shownAckSchema,
    );
  } catch {
    // See above.
  }
};

/**
 * Statuses that mean "there is no drawing for this design, and there never will
 * be one for this request" as opposed to "something went wrong".
 *
 *  404 — the raw bytes are past their 7-day horizon (`no_raw`), or the
 *        in-memory link registry was rebuilt from a truncated replay and no
 *        longer knows this handle (`unknown_link`).
 *  409 / 413 / 422 — the server refused to build a scene it could not build
 *        HONESTLY: a frame past the projector's node ceiling is either drawn
 *        with a hole or not drawn at all, and "not at all" is the only one of
 *        those two a user can calibrate. A refusal reads as "no drawing", never
 *        as an error.
 *
 * Everything else is `failed`, which says a different sentence: the words Loom
 * read are still there.
 */
const NO_DRAWING_STATUS: ReadonlySet<number> = new Set([404, 409, 413, 422]);

/**
 * The 404s that are about the HANDLE rather than about the design.
 *
 * `no_raw` / `no_drawing` mean "Loom kept no layout for this design", which is
 * what `focus.figma.preview.noDrawing` says. `unknown_link` means something
 * else entirely: the in-process link registry was rebuilt (an instance recycle)
 * and can no longer say which frame this chip's handle addresses — the bytes
 * may well be sitting on the row, and the sibling frame chip beside it serves a
 * 200 off exactly that row. A whole-file chip is the case that cannot recover,
 * because the read's node id is deliberately not in the event log, so after a
 * recycle a restored chat offers "Show the sketch" on it and — until this set
 * existed — was told the layout had not been kept, which is false.
 * `unknown_session` is the same class one level up: the session, not the link.
 *
 * `server.ts` already calls that sentence out as false for a different reason
 * (a deployment with Figma on and the preview off). This is the second.
 */
const LOST_HANDLE_REASONS: ReadonlySet<string> = new Set([
  "unknown_link",
  "unknown_session",
]);

/**
 * The route's own `reason` token, off the error body.
 *
 * `HttpError` carries the raw body for exactly this — a caller that wants to
 * say something specific parses it rather than scraping `message`, which is a
 * debug string. Anything that is not a JSON object with a string `reason` is
 * `null`, and `null` keeps the old, broader sentence.
 */
const reasonOf = (err: unknown): string | null => {
  if (!(err instanceof HttpError) || err.body === undefined) return null;
  try {
    const parsed: unknown = JSON.parse(err.body);
    if (typeof parsed !== "object" || parsed === null) return null;
    const reason = (parsed as { readonly reason?: unknown }).reason;
    return typeof reason === "string" ? reason : null;
  } catch {
    return null;
  }
};

/**
 * The sketch for one design: the chat's own copy when there is one, the route
 * otherwise.
 *
 * `persisted` is the picture THIS conversation had — `ws.figmaSceneFor(handle)`,
 * folded from the replayed `figma_scene` event. Non-null and the hook never
 * touches the network.
 *
 * DERIVED, not initialised. The scene can arrive AFTER mount: the flow step and
 * the chip both render the moment their handle exists, and on a fresh live read
 * the event lands a beat later (the server raises the ask as soon as the row
 * resolves and projects the draw list behind it). A `useState` initialiser runs
 * once, so seeding from it alone would leave the first mount fetching forever
 * and silently prefer the GET's newer bytes. `persisted` is therefore a DEP:
 * when it lands, the previous effect's cleanup aborts the in-flight GET and this
 * one returns before creating another. The initialiser below is an optimisation
 * on top of that — it spares a mount that already has the scene one frame of
 * `loading`, and it is not what makes a late arrival win.
 *
 * On the GET lane: aborts on unmount and on a handle change, so a slow response
 * cannot land on a screen that has moved on. Never throws: every failure
 * resolves to a STATE WORD, because in question mode a drawing that cannot
 * render must still leave a way forward rather than a blank card.
 */
export const useDesignScene = (
  sessionId: string | null,
  previewId: string | null,
  persisted: DesignScene | null = null,
): DesignSceneState => {
  const [state, setState] = useState<DesignSceneState>(() =>
    persisted !== null
      ? { status: "ready", scene: persisted }
      : { status: "loading", scene: null },
  );
  /**
   * The handle this mount has already told the server about.
   *
   * ONE signal per open, matching what the GET lane costs: a mount is an open
   * (the chat panel is behind a button, the flow step mounts when the question
   * is raised), and a close unmounts, so reopening says it again — correctly,
   * because the user is looking at it again. Inside a single open it must stay
   * at one: the full replay on every attach hands back a NEW scene object for
   * the same bytes, so `persisted` changes identity per reconnect and this
   * effect re-runs. A ref rather than the server's own dedupe because the
   * cheapest request is the one never sent.
   */
  const noted = useRef<string | null>(null);
  useEffect(() => {
    if (persisted !== null) {
      // Functional, and identity-compared: the full replay on every attach
      // re-parses the event and hands back a NEW scene object for the same
      // bytes. Without this the widget would re-render — and `FigmaPreview`'s
      // `scene === null` modal-close effect would re-run — once per reconnect
      // for a picture that did not change.
      setState((prev) =>
        prev.status === "ready" && prev.scene === persisted
          ? prev
          : { status: "ready", scene: persisted },
      );
      // The GET's side effect, said out loud on the lane that has no GET. It is
      // NOT in the cleanup and NOT awaited: the picture is already on screen,
      // and nothing about the drawing waits on this.
      if (sessionId !== null && previewId !== null) {
        const key = `${sessionId}\u0000${previewId}`;
        if (noted.current !== key) {
          noted.current = key;
          void notePreviewShown(sessionId, previewId);
        }
      }
      return;
    }
    if (sessionId === null || previewId === null) {
      setState({ status: "empty", scene: null });
      return;
    }
    const ctrl = new AbortController();
    let live = true;
    setState({ status: "loading", scene: null });
    void (async () => {
      try {
        const scene = await httpGet(
          designScenePath(sessionId, previewId),
          designSceneSchema,
          { cache: "no-store", signal: ctrl.signal },
        );
        if (!live) return;
        setState({ status: "ready", scene });
      } catch (err) {
        if (!live || ctrl.signal.aborted) return;
        const status = err instanceof HttpError ? err.status : 0;
        if (!NO_DRAWING_STATUS.has(status)) {
          setState({ status: "failed", scene: null });
          return;
        }
        const reason = reasonOf(err);
        setState({
          status:
            reason !== null && LOST_HANDLE_REASONS.has(reason)
              ? "lost"
              : "empty",
          scene: null,
        });
      }
    })();
    return () => {
      live = false;
      ctrl.abort();
    };
  }, [sessionId, previewId, persisted]);
  return state;
};
